import React, { lazy, Suspense, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Lazy load icons (only load them when this page is loaded)
const FaMapMarkerAlt = lazy(() =>
  import("react-icons/fa").then((m) => ({ default: m.FaMapMarkerAlt }))
);
const FaClock = lazy(() =>
  import("react-icons/fa").then((m) => ({ default: m.FaClock }))
);
const FaEnvelope = lazy(() =>
  import("react-icons/fa").then((m) => ({ default: m.FaEnvelope }))
);
const FaPhoneAlt = lazy(() =>
  import("react-icons/fa").then((m) => ({ default: m.FaPhoneAlt }))
);

// Lazy load image instead of importing directly
const living = new URL("../Assets/about/contact-bann-new.webp", import.meta.url)
  .href;


gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
    const formRef = useRef();
  const heroRef = useRef(null);


  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    // Extra validation
    const phone = formData.get("phone");
    const email = formData.get("email");

    // Phone must be exactly 10 digits
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(phone)) {
      alert("❌ Please enter a valid 10-digit phone number.");
      return;
    }

    // Email basic validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert("❌ Please enter a valid email address.");
      return;
    }

    // Send to mail.php
    const response = await fetch("/mail.php", {
      method: "POST",
      body: formData,
    });

    if (response.ok) {
      alert("Thank you for your query. We will get back to you at the earliest.");
      formRef.current.reset(); // clear form after submit
    } else {
      alert("❌ Failed to send message. Try again later.");
    }
  };
  // Initialize animations
  useEffect(() => {
    // Hero section animation
    gsap.from(heroRef.current, {
      opacity: 0,
      y: 100,
      duration: 1,
      scrollTrigger: {
        trigger: heroRef.current,
        start: "top bottom",
        toggleActions: "play none none none",
      },
    });
    // Clean up ScrollTrigger instances
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);
  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
        <section
        ref={heroRef}
        className="main-home-section relative h-[100vh] flex items-end justify-start overflow-hidden"
      >
        {/* Lazy load image */}
        <img
          src={living}
          alt="Contact Banner"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="text-left z-10 px-4 pb-[40px]">
          <div className="floating-element mx-auto bg-opacity-20 backdrop-blur-smp-6 max-w-md">
            <h1
              className="md:text-7xl text-5xl font-bold text-white mb-6"
              style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.8)" }}
            >
              Contact Us
            </h1>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-10 px-4">
        <div className="md:max-w-7xl w-full mx-auto border border-[#727272] rounded-2xl grid md:grid-cols-2">
          {/* Left - Contact Info */}
          <div className="space-y-6 md:border-r md:border-b-0 border-b border-[#727272] md:p-10 p-4">
            <h2 className="h2 capitalize 2xl:text-[55px] xl:text-[48px] lg:text-[46px] md:text-[40px] text-[30px]  font-bold mb-4">
              Visit us
            </h2>

            {/* Location */}
              <Suspense fallback={<div>Loading...</div>}>
              {/* Location */}
              <div className="flex items-center gap-3">
                <FaMapMarkerAlt className="text-black-500 mt-1 text-3xl" />
                <a
                  href="https://www.google.com/maps?q=NH-205,+Kharar-+Kurali+Highway,+Punjab,+14030"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="md:text-[18px] text-[16px] font-bold leading-6 hover:underline"
                >
                  NH-205, Kharar- Kurali Highway, Punjab, 140301
                </a>
              </div>

              {/* Office Hours */}
              <div className="flex items-center gap-3">
                <FaClock className="text-black-500 mt-1 text-3xl" />
                <p className="md:text-[18px] text-[16px] font-bold leading-6">
                  Monday – Friday, 9:00 AM – 6:00 PM
                </p>
              </div>

              {/* Email */}
              <div className="flex items-center gap-3">
                <FaEnvelope className="text-black-500 mt-1 text-3xl" />
                <a
                  href="mailto:info@townbell.in"
                  className="md:text-[18px] text-[16px] font-bold leading-6 hover:underline"
                >
                  info@townbell.in
                </a>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-3">
                <FaPhoneAlt className="text-black-500 mt-1 text-3xl" />
                <a
                  href="tel:+918264411339"
                  className="md:text-[18px] text-[16px] font-bold leading-6 hover:underline"
                >
                  (+91) 82644-11339
                </a>
              </div>
              <div className="flex items-center gap-3">
                <FaPhoneAlt className="text-black-500 mt-1 text-3xl" />
                <a
                  href="tel:+917341113338"
                  className="md:text-[18px] text-[16px] font-bold leading-6 hover:underline"
                >
                  (+91) 734-1113338 
                </a>
              </div>
            </Suspense>
            
          </div>

          {/* Right - Contact Form */}
          <div className="md:p-10 p-4">
            <h2 className="h2 capitalize text-2xl md:text-4xl font-bold mb-6">
              Question? Send us a message. Don’t be shy
            </h2>

            <form className="space-y-4" ref={formRef} onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <input
                   type="text"
                name="name"
                placeholder="Name"
                required
                    className="bg-transparent p-3 border border-gray-400 focus:border-gray-500"
                  />
                </div>
                <div className="flex flex-col">
                  <input
                    type="number"
                name="phone"
                placeholder="Phone"
                required
                  pattern="[0-9]{10}"  // HTML5 validation
                title="Phone number must be 10 digits"
                    className="bg-transparent p-3 border border-gray-400 focus:border-gray-500"
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <input
                  type="email"
              name="email"
              placeholder="Email"
              required
                  className="bg-transparent p-3 border border-gray-400 focus:border-gray-500"
                />
              </div>

              <div className="flex flex-col">
                <textarea
                  name="message"
              rows={5}
              placeholder="Message"
              required
                  className="bg-transparent p-3 border border-gray-400 focus:border-gray-500"
                ></textarea>
              </div>

              <button
                type="submit"
                className="bg-black hover:bg-gray-600 text-white font-medium py-3 w-full transition"
                 
              >
                Submit
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
